import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useAuth } from "../../context/AuthContext";

const PasswordManager = ({ userData }) => {
  const { addPassword } = useAuth();
  const [newPassword, setNewPassword] = useState({
    title: "",
    username: "",
    password: "",
    website: "",
    notes: "",
  });
  const [showPasswordForm, setShowPasswordForm] = useState(false);
  const [showPassword, setShowPassword] = useState({});
  const [editingId, setEditingId] = useState(null);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setNewPassword((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (editingId) {
      // Update existing password
      const updatedPasswords = userData.passwords.map((pwd) =>
        pwd.id === editingId
          ? {
              ...newPassword,
              id: editingId,
              lastUpdated: new Date().toISOString(),
            }
          : pwd
      );
      addPassword(updatedPasswords);
    } else {
      // Add new password
      addPassword([
        ...userData.passwords,
        {
          ...newPassword,
          id: `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
          createdAt: new Date().toISOString(),
          lastUpdated: new Date().toISOString(),
        },
      ]);
    }
    setNewPassword({
      title: "",
      username: "",
      password: "",
      website: "",
      notes: "",
    });
    setShowPasswordForm(false);
    setEditingId(null);
  };

  const handleEdit = (password) => {
    setNewPassword(password);
    setEditingId(password.id);
    setShowPasswordForm(true);
  };

  return (
    <motion.div
      className="password-manager"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5 }}
    >
      <div className="section-header">
        <motion.h2 layout>Password Manager</motion.h2>
        <motion.button
          className="add-button"
          onClick={() => setShowPasswordForm(!showPasswordForm)}
          whileHover={{ y: -2, boxShadow: "0 4px 8px rgba(0,0,0,0.1)" }}
          whileTap={{ scale: 0.98 }}
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
        >
          {showPasswordForm ? (
            <>
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
              >
                <line x1="18" y1="6" x2="6" y2="18"></line>
                <line x1="6" y1="6" x2="18" y2="18"></line>
              </svg>
              Cancel
            </>
          ) : (
            <>
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
              >
                <line x1="12" y1="5" x2="12" y2="19"></line>
                <line x1="5" y1="12" x2="19" y2="12"></line>
              </svg>
              Add Password
            </>
          )}
        </motion.button>
      </div>

      <AnimatePresence>
        {showPasswordForm && (
          <motion.div
            className="password-form"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3 }}
          >
            <form onSubmit={handleSubmit}>
              <motion.div
                className="form-group"
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.1 }}
              >
                <label>Title</label>
                <input
                  type="text"
                  name="title"
                  value={newPassword.title}
                  onChange={handleInputChange}
                  required
                  placeholder="e.g. Facebook Account"
                />
              </motion.div>

              <motion.div
                className="form-group"
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.15 }}
              >
                <label>Username/Email</label>
                <input
                  type="text"
                  name="username"
                  value={newPassword.username}
                  onChange={handleInputChange}
                  required
                  placeholder="your@email.com"
                />
              </motion.div>

              <motion.div
                className="form-group"
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.2 }}
              >
                <label>Password</label>
                <div className="password-input-wrapper">
                  <input
                    type={showPassword.form ? "text" : "password"}
                    name="password"
                    value={newPassword.password}
                    onChange={handleInputChange}
                    required
                    placeholder="••••••••"
                  />
                  <button
                    type="button"
                    className="toggle-password"
                    onClick={() =>
                      setShowPassword({
                        ...showPassword,
                        form: !showPassword.form,
                      })
                    }
                  >
                    <svg
                      width="20"
                      height="20"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                    >
                      {showPassword.form ? (
                        <>
                          <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"></path>
                          <line x1="1" y1="1" x2="23" y2="23"></line>
                        </>
                      ) : (
                        <>
                          <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                          <circle cx="12" cy="12" r="3"></circle>
                        </>
                      )}
                    </svg>
                  </button>
                </div>
              </motion.div>

              <motion.div
                className="form-group"
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.25 }}
              >
                <label>Website</label>
                <input
                  type="url"
                  name="website"
                  value={newPassword.website}
                  onChange={handleInputChange}
                  placeholder="https://example.com"
                />
              </motion.div>

              <motion.div
                className="form-group"
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.3 }}
              >
                <label>Notes</label>
                <textarea
                  name="notes"
                  value={newPassword.notes}
                  onChange={handleInputChange}
                  placeholder="Any additional information..."
                  rows="3"
                />
              </motion.div>

              <motion.div
                className="form-actions"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.35 }}
              >
                <motion.button
                  type="submit"
                  className="save-button"
                  whileHover={{
                    scale: 1.03,
                    boxShadow: "0 4px 12px rgba(79, 70, 229, 0.3)",
                  }}
                  whileTap={{ scale: 0.98 }}
                >
                  {editingId ? "Update Password" : "Save Password"}
                </motion.button>
              </motion.div>
            </form>
          </motion.div>
        )}
      </AnimatePresence>

      <motion.div layout className="passwords-list">
        <AnimatePresence>
          {userData?.passwords?.length > 0 ? (
            userData.passwords.map((password) => (
              <motion.div
                key={password.id}
                className="password-card"
                layout
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ type: "spring", damping: 20 }}
                whileHover={{ y: -5, boxShadow: "0 8px 16px rgba(0,0,0,0.1)" }}
              >
                <div className="password-header">
                  <h3>{password.title}</h3>
                  <div className="password-actions">
                    <button
                      className="edit-btn"
                      onClick={() => handleEdit(password)}
                    >
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                      >
                        <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                        <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
                      </svg>
                    </button>
                  </div>
                </div>

                <div className="password-details">
                  <div className="detail-row">
                    <span>Username:</span>
                    <span>{password.username}</span>
                  </div>

                  <div className="detail-row">
                    <span>Password:</span>
                    <div className="password-value">
                      {showPassword[password.id] ? (
                        <span>{password.password}</span>
                      ) : (
                        <span className="password-mask">••••••••</span>
                      )}
                      <button
                        className="toggle-password"
                        onClick={() =>
                          setShowPassword({
                            ...showPassword,
                            [password.id]: !showPassword[password.id],
                          })
                        }
                      >
                        <svg
                          width="18"
                          height="18"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                        >
                          {showPassword[password.id] ? (
                            <>
                              <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"></path>
                              <line x1="1" y1="1" x2="23" y2="23"></line>
                            </>
                          ) : (
                            <>
                              <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                              <circle cx="12" cy="12" r="3"></circle>
                            </>
                          )}
                        </svg>
                      </button>
                    </div>
                  </div>

                  {password.website && (
                    <div className="detail-row">
                      <span>Website:</span>
                      <a
                        href={
                          password.website.startsWith("http")
                            ? password.website
                            : `https://${password.website}`
                        }
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {password.website.replace(/^https?:\/\//, "")}
                      </a>
                    </div>
                  )}

                  {password.notes && (
                    <div className="detail-row notes">
                      <span>Notes:</span>
                      <p>{password.notes}</p>
                    </div>
                  )}
                </div>

                <div className="password-footer">
                  <small>
                    Last updated:{" "}
                    {new Date(password.lastUpdated).toLocaleDateString()}
                  </small>
                </div>
              </motion.div>
            ))
          ) : (
            <motion.div
              className="empty-state"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.2 }}
            >
              <svg
                width="48"
                height="48"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
              >
                <path d="M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z"></path>
              </svg>
              <p>No passwords saved yet</p>
              <motion.button
                className="add-first-btn"
                onClick={() => setShowPasswordForm(true)}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.98 }}
              >
                Add Your First Password
              </motion.button>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>
    </motion.div>
  );
};

export default PasswordManager;
